// Bilingual (English / Spanish) content for Chato Home Services.
// Keys are grouped by area. Access via the t('group.key') helper.

export const translations = {
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      students: 'Students',
      about: 'About',
      contact: 'Contact',
      requestQuote: 'Request a Quote',
      toggleAria: 'Switch language',
    },
    home: {
      heroTitle: '24/7 Home Service Done Right',
      heroSubtitle:
        'A skilled professional delivering premium craftsmanship with upfront pricing and guaranteed satisfaction.',
      requestQuote: 'Request a Quote',
      callButton: 'Call 555-555-5555',
      popularServices: 'Popular Services',
      viewAllServices: 'View All Services',
      reviewsSuffix: 'reviews',
      howItWorks: 'How It Works',
      areasWeServe: 'Areas We Serve',
      areasWeServeSubtitle:
        "Proudly serving California's Central Coast, from San Luis Obispo down to Santa Barbara, including Pismo Beach, Santa Maria, and Lompoc.",
      customersSay: 'What Our Customers Say',
      readyTitle: 'Ready to Get Started?',
      readySubtitle: 'Get your free quote today and experience the Chato difference.',
      callNow: 'Call Now',
      studentBannerTitle: 'Cal Poly & UCSB Students',
      studentBannerText:
        'Move-out coming up? Get quick, discreet repairs so you can get your deposit back. Hassle-free fixes scheduled around your finals.',
      studentBannerButton: 'Student Repairs',
    },
    students: {
      heroTitle: 'Student Move-Out Repairs',
      heroSubtitle:
        'Quick, discreet, hassle-free repairs for Cal Poly SLO and UCSB students. Get your rental move-out ready and protect your security deposit.',
      heroButton: 'Request a Quote',
      heroCall: 'Call Now',
      whyTitle: 'Why Students Choose Chato',
      why: {
        discreet: {
          title: 'Discreet & Professional',
          description: 'Low-key, respectful service. I show up, do the work cleanly, and keep it simple.',
        },
        deposit: {
          title: 'Protect Your Deposit',
          description: 'Move-out ready repairs that help you avoid deductions and get your deposit back.',
        },
        schedule: {
          title: 'Scheduled Around You',
          description: 'Flexible timing around finals, move-out week, and busy student calendars.',
        },
        fast: {
          title: 'Fast Turnaround',
          description: 'Most small fixes handled quickly so you are not left scrambling before inspection.',
        },
      },
      fixesTitle: 'Common Student Fixes',
      fixesSubtitle: 'The everyday wear and tear that comes up in dorms, apartments, and rentals.',
      fixes: [
        'Drywall holes and dents patched',
        'Paint touch-ups and color matching',
        'Broken or bent blinds repaired or replaced',
        'Leaky faucets and running toilets',
        'Loose or broken cabinet doors and handles',
        'Towel bars, shelves, and mounts reattached',
        'Door and closet repairs',
        'General wear-and-tear touch-ups before inspection',
      ],
      areasNote: 'Serving students in San Luis Obispo (Cal Poly) and Santa Barbara / Isla Vista (UCSB).',
      ctaTitle: 'Finals Week Fixes, Handled',
      ctaSubtitle: "Tell me what needs fixing and I'll get you a quote fast. No hassle, no judgment.",
      ctaButton: 'Request a Quote',
    },
    footer: {
      tagline: 'Premium handyman services you can trust. Committed to quality and excellence.',
      serviceHours: 'Service Hours',
      hoursValue: '24/7 • 365 Days',
      location: 'Central Coast',
      emergencyTitle: 'Emergency Services',
      emergencyText:
        "Need immediate assistance? We're available 24/7 for emergency repairs and urgent situations.",
      callNow: 'Call Now: 555-555-5555',
      rights: 'All rights reserved.',
      badges: 'Quality • Reliable • Trusted',
    },
    services: {
      title: 'Our Services',
      subtitle: 'Professional handyman services with transparent pricing and guaranteed satisfaction.',
      whatsIncluded: "What's Included:",
      requestQuoteFor: 'Request Quote for',
      projectGallery: 'Project Gallery',
      recentReviews: 'Recent Reviews',
      reviewsSuffix: 'reviews',
      customTitle: 'Need a Custom Solution?',
      customSubtitle:
        "Don't see exactly what you need? We handle all types of home repairs and improvements.",
      customButton: 'Get Custom Quote',
    },
    about: {
      title: 'About Chato',
      subtitle: 'The story behind our commitment to excellence and premium service.',
      ownerAlt: 'Chato - Owner and Founder',
      headingCompany: 'About Chato Home Services',
      body1:
        "Founded in 2020 right after I graduated from Pioneer Valley High School, Chato Home Services started with a simple goal: to provide honest, reliable home repairs across the Central Coast. As a one-person operation, I handle every job personally from start to finish.",
      body2:
        "I believe your home deserves the best care possible. That's why I focus on providing not just repairs, but solutions that last. I treat every home I work in with the same respect I'd show my own.",
      body3:
        'My approach means I take on a limited number of clients so each one gets my full attention and highest quality work. I\'m not the cheapest option – I\'m the best value, delivering premium results that save you money in the long run.',
      body4:
        "Available 24/7 for emergencies and offering upfront pricing on every project, I've eliminated the uncertainty and stress that usually come with home repairs. When you call Chato, you're working directly with the person doing the work – someone who cares about your home as much as you do.",
      ourValues: 'My Values',
      ourJourney: 'My Journey',
      whyChoose: 'Why Choose Chato?',
      teamAlt: 'California Central Coast, Santa Maria Valley',
      ctaTitle: 'Experience the Chato Difference',
      ctaSubtitle: 'Join the many satisfied customers who trust me with their homes.',
      ctaButton: 'Request a Quote',
      values: {
        excellence: { title: 'Excellence', description: 'We deliver superior craftsmanship on every project, no matter the size.' },
        trust: { title: 'Trust', description: 'Honest, dependable service for your complete peace of mind.' },
        reliability: { title: 'Reliability', description: '24/7 availability with prompt response times when you need us most.' },
        expertise: { title: 'Expertise', description: 'A skilled professional with hands-on experience across all trades.' },
      },
      timeline: {
        founded: { title: 'Founded', description: 'Graduated from Pioneer Valley High School and started Chato Home Services with a vision of honest, premium home services.' },
        service247: { title: '24/7 Service', description: 'Began offering round-the-clock emergency service for urgent repairs.' },
        smartHome: { title: 'Smart Home Focus', description: 'Added smart home installation and automation services.' },
        premium: { title: 'Premium Standard', description: "Established as the Central Coast's trusted, exclusive handyman service." },
      },
      reasons: {
        trusted: { primary: 'Trusted & Dependable', secondary: 'Honest, reliable service you can count on for your protection' },
        emergency: { primary: '24/7 Emergency Service', secondary: 'Available around the clock for urgent repairs' },
        pricing: { primary: 'Upfront Pricing', secondary: 'No hidden fees or surprise charges' },
        craftsmen: { primary: 'Skilled Craftsmanship', secondary: 'Hands-on experience across all major trades' },
        training: { primary: 'Continuous Training', secondary: 'Staying current with the latest techniques and technologies' },
      },
    },
    contact: {
      title: 'Request a Quote',
      subtitle: "Tell us about your project and we'll provide a detailed quote within 24 hours.",
      preferredMethod: 'Preferred Contact Method *',
      email: 'Email',
      phone: 'Phone',
      both: 'Both',
      emailLabel: 'Email Address',
      phoneLabel: 'Phone Number',
      servicesNeeded: 'Services Needed *',
      selectAll: 'Select all services that apply to your project',
      other: 'Other',
      otherService: 'Other Service',
      specifyOther: 'Please specify other service',
      projectPhotos: 'Project Photos (Optional)',
      uploadHelp: 'Upload photos to help us better understand your project',
      uploadImages: 'Upload Images',
      projectDetails: 'Project Details',
      projectDetailsPlaceholder:
        'Please describe your project in detail, including any specific requirements, timeline, or concerns...',
      sendRequest: 'Send Request',
      urgentTitle: 'Urgent? Call Now',
      urgentText: 'For emergency repairs and urgent situations',
      whatToExpect: 'What to Expect',
      responseTimeLabel: 'Response Time:',
      responseTimeText:
        "We'll contact you within 2 hours during business hours, or first thing the next morning for after-hours requests.",
      quoteDeliveryLabel: 'Quote Delivery:',
      quoteDeliveryText: 'Detailed written quotes provided within 24 hours of initial contact.',
      schedulingLabel: 'Scheduling:',
      schedulingText: 'Most projects can be scheduled within 48-72 hours of quote approval.',
      selectedServices: 'Selected Services',
      successMessage: "Request submitted successfully! We'll contact you within 2 hours.",
      errors: {
        emailRequired: 'Email is required',
        phoneRequired: 'Phone number is required',
        emailInvalid: 'Please enter a valid email address',
        phoneInvalid: 'Please enter a valid phone number',
        servicesRequired: 'Please select at least one service or specify other',
      },
    },
    trustIndicators: {
      response: { title: '24/7 Response', description: 'Always available when you need us most' },
      quotes: { title: 'Upfront Quotes', description: 'No surprises, transparent pricing' },
      craftsmanship: { title: 'Skilled Craftsmanship', description: 'A skilled professional with hands-on experience' },
      respectful: { title: 'Respectful & Clean', description: 'I treat your home like my own' },
    },
    howItWorks: {
      request: { title: 'Request', description: 'Tell us about your project and preferred contact method' },
      schedule: { title: 'Schedule', description: "We'll provide a quote and schedule at your convenience" },
      done: { title: 'Done', description: 'Professional work completed to your satisfaction' },
    },
    servicesData: {
      plumbing: {
        title: 'Plumbing Repairs',
        description: 'From leaky faucets to pipe repairs, we handle all your plumbing needs with precision and care.',
        bullets: ['Leak detection and repair', 'Faucet and fixture installation', 'Drain cleaning and unclogging', 'Water heater maintenance'],
      },
      electrical: {
        title: 'Electrical Services',
        description: 'Safe, reliable electrical work for your home. Non-permit electrical repairs and installations.',
        bullets: ['Outlet and switch installation', 'Light fixture mounting', 'Ceiling fan installation', 'Electrical troubleshooting'],
      },
      drywall: {
        title: 'Drywall & Painting',
        description: 'Professional drywall repair and interior painting to refresh and restore your space.',
        bullets: ['Hole and crack repair', 'Texture matching', 'Interior painting', 'Trim and baseboard work'],
      },
      flooring: {
        title: 'Flooring Repair',
        description: 'Expert flooring repairs and installations for hardwood, tile, and laminate surfaces.',
        bullets: ['Hardwood plank replacement', 'Tile repair and regrouting', 'Laminate installation', 'Subfloor repairs'],
      },
      smartHome: {
        title: 'Smart Home Installation',
        description: 'Modern smart home device installation and setup for enhanced convenience and security.',
        bullets: ['Smart thermostat installation', 'Security camera mounting', 'Smart doorbell setup', 'Home automation integration'],
      },
      emergency: {
        title: 'Emergency Services',
        description: '24/7 emergency response for urgent home repairs and water damage mitigation.',
        bullets: ['Emergency leak repair', 'Water damage mitigation', 'Power outage troubleshooting', 'Storm damage assessment'],
      },
    },
    reviewsData: {
      r1: 'Chato was incredible! He fixed our kitchen plumbing issue within hours of calling. Professional, clean, and reasonably priced.',
      r2: 'Outstanding electrical work. They installed our new ceiling fans and fixed several outlet issues. Highly recommend!',
      r3: "The drywall repair was flawless - you can't even tell there was damage. The painting job looks amazing too.",
      r4: 'Great service for our hardwood floor repair. They matched the existing wood perfectly and the work area was left spotless.',
      r5: 'Smart home installation was seamless. They set up our entire security system and taught us how to use everything.',
      r6: 'Called for an emergency water leak at 2 AM. They were here within 30 minutes and had it fixed quickly. Lifesavers!',
      r7: 'Exceptional service from start to finish. Professional, punctual, and the quality of work exceeded expectations.',
      r8: 'Very satisfied with the electrical outlet installation. Clean work and fair pricing. Will definitely use again.',
    },
  },
  es: {
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      students: 'Estudiantes',
      about: 'Nosotros',
      contact: 'Contacto',
      requestQuote: 'Solicitar Cotización',
      toggleAria: 'Cambiar idioma',
    },
    home: {
      heroTitle: 'Servicio para el Hogar 24/7 Bien Hecho',
      heroSubtitle:
        'Un profesional capacitado que ofrece trabajo de primera calidad con precios claros y satisfacción garantizada.',
      requestQuote: 'Solicitar Cotización',
      callButton: 'Llame al 555-555-5555',
      popularServices: 'Servicios Populares',
      viewAllServices: 'Ver Todos los Servicios',
      reviewsSuffix: 'reseñas',
      howItWorks: 'Cómo Funciona',
      areasWeServe: 'Áreas que Atendemos',
      areasWeServeSubtitle:
        'Con orgullo servimos la Costa Central de California, desde San Luis Obispo hasta Santa Bárbara, incluyendo Pismo Beach, Santa Maria y Lompoc.',
      customersSay: 'Lo que Dicen Nuestros Clientes',
      readyTitle: '¿Listo para Comenzar?',
      readySubtitle: 'Obtenga su cotización gratis hoy y viva la diferencia Chato.',
      callNow: 'Llame Ahora',
      studentBannerTitle: 'Estudiantes de Cal Poly y UCSB',
      studentBannerText:
        '¿Se acerca la mudanza? Obtenga reparaciones rápidas y discretas para recuperar su depósito. Arreglos sin complicaciones agendados según sus exámenes finales.',
      studentBannerButton: 'Reparaciones para Estudiantes',
    },
    students: {
      heroTitle: 'Reparaciones de Mudanza para Estudiantes',
      heroSubtitle:
        'Reparaciones rápidas, discretas y sin complicaciones para estudiantes de Cal Poly SLO y UCSB. Deje su renta lista para la mudanza y proteja su depósito de seguridad.',
      heroButton: 'Solicitar Cotización',
      heroCall: 'Llame Ahora',
      whyTitle: 'Por Qué los Estudiantes Eligen a Chato',
      why: {
        discreet: {
          title: 'Discreto y Profesional',
          description: 'Servicio discreto y respetuoso. Llego, hago el trabajo de forma limpia y lo mantengo sencillo.',
        },
        deposit: {
          title: 'Proteja su Depósito',
          description: 'Reparaciones listas para la mudanza que le ayudan a evitar descuentos y recuperar su depósito.',
        },
        schedule: {
          title: 'Agendado a su Medida',
          description: 'Horarios flexibles alrededor de los exámenes finales, la semana de mudanza y la agenda estudiantil.',
        },
        fast: {
          title: 'Respuesta Rápida',
          description: 'La mayoría de los arreglos pequeños se resuelven rápido para que no corra antes de la inspección.',
        },
      },
      fixesTitle: 'Arreglos Comunes para Estudiantes',
      fixesSubtitle: 'El desgaste cotidiano que surge en dormitorios, apartamentos y rentas.',
      fixes: [
        'Reparación de hoyos y abolladuras en tablarroca',
        'Retoques de pintura e igualación de color',
        'Persianas rotas o dobladas reparadas o reemplazadas',
        'Llaves que gotean e inodoros que corren',
        'Puertas y manijas de gabinete sueltas o rotas',
        'Toalleros, repisas y soportes reinstalados',
        'Reparación de puertas y clósets',
        'Retoques de desgaste general antes de la inspección',
      ],
      areasNote: 'Atendiendo a estudiantes en San Luis Obispo (Cal Poly) y Santa Bárbara / Isla Vista (UCSB).',
      ctaTitle: 'Arreglos para la Semana de Finales, Resueltos',
      ctaSubtitle: 'Cuénteme qué necesita arreglo y le daré una cotización rápido. Sin complicaciones, sin juicios.',
      ctaButton: 'Solicitar Cotización',
    },
    footer: {
      tagline: 'Servicios de manitas de confianza. Comprometidos con la calidad y la excelencia.',
      serviceHours: 'Horario de Servicio',
      hoursValue: '24/7 • 365 Días',
      location: 'Costa Central',
      emergencyTitle: 'Servicios de Emergencia',
      emergencyText:
        '¿Necesita ayuda inmediata? Estamos disponibles 24/7 para reparaciones de emergencia y situaciones urgentes.',
      callNow: 'Llame Ahora: 555-555-5555',
      rights: 'Todos los derechos reservados.',
      badges: 'Calidad • Confiable • De Confianza',
    },
    services: {
      title: 'Nuestros Servicios',
      subtitle: 'Servicios profesionales de manitas con precios transparentes y satisfacción garantizada.',
      whatsIncluded: 'Qué Incluye:',
      requestQuoteFor: 'Solicitar Cotización de',
      projectGallery: 'Galería del Proyecto',
      recentReviews: 'Reseñas Recientes',
      reviewsSuffix: 'reseñas',
      customTitle: '¿Necesita una Solución Personalizada?',
      customSubtitle:
        '¿No encuentra exactamente lo que necesita? Realizamos todo tipo de reparaciones y mejoras para el hogar.',
      customButton: 'Obtener Cotización Personalizada',
    },
    about: {
      title: 'Acerca de Chato',
      subtitle: 'La historia detrás de nuestro compromiso con la excelencia y el servicio premium.',
      ownerAlt: 'Chato - Dueño y Fundador',
      headingCompany: 'Acerca de Chato Home Services',
      body1:
        'Fundada en 2020, justo después de graduarme de Pioneer Valley High School, Chato Home Services comenzó con una meta sencilla: ofrecer reparaciones del hogar honestas y confiables en toda la Costa Central. Como un negocio de una sola persona, me encargo personalmente de cada trabajo de principio a fin.',
      body2:
        'Creo que su hogar merece el mejor cuidado posible. Por eso me enfoco en brindar no solo reparaciones, sino soluciones duraderas. Trato cada hogar en el que trabajo con el mismo respeto que el mío.',
      body3:
        'Mi enfoque significa que acepto una cantidad limitada de clientes para que cada uno reciba toda mi atención y el trabajo de mayor calidad. No soy la opción más barata, soy el mejor valor, entregando resultados premium que le ahorran dinero a largo plazo.',
      body4:
        'Disponible 24/7 para emergencias y con precios claros en cada proyecto, he eliminado la incertidumbre y el estrés que suelen acompañar las reparaciones del hogar. Cuando llama a Chato, trabaja directamente con la persona que hace el trabajo, alguien que cuida su hogar tanto como usted.',
      ourValues: 'Mis Valores',
      ourJourney: 'Mi Trayectoria',
      whyChoose: '¿Por Qué Elegir a Chato?',
      teamAlt: 'Costa Central de California, Valle de Santa Maria',
      ctaTitle: 'Viva la Diferencia Chato',
      ctaSubtitle: 'Únase a los muchos clientes satisfechos que confían en mí para su hogar.',
      ctaButton: 'Solicitar Cotización',
      values: {
        excellence: { title: 'Excelencia', description: 'Ofrecemos trabajo superior en cada proyecto, sin importar el tamaño.' },
        trust: { title: 'Confianza', description: 'Servicio honesto y confiable para su completa tranquilidad.' },
        reliability: { title: 'Fiabilidad', description: 'Disponibilidad 24/7 con tiempos de respuesta rápidos cuando más nos necesita.' },
        expertise: { title: 'Experiencia', description: 'Un profesional capacitado con experiencia práctica en todos los oficios.' },
      },
      timeline: {
        founded: { title: 'Fundación', description: 'Me gradué de Pioneer Valley High School y fundé Chato Home Services con la visión de servicios para el hogar honestos y premium.' },
        service247: { title: 'Servicio 24/7', description: 'Comencé a ofrecer servicio de emergencia las 24 horas para reparaciones urgentes.' },
        smartHome: { title: 'Enfoque en Hogar Inteligente', description: 'Agregué servicios de instalación y automatización de hogar inteligente.' },
        premium: { title: 'Estándar Premium', description: 'Establecido como el servicio de manitas exclusivo y de confianza de la Costa Central.' },
      },
      reasons: {
        trusted: { primary: 'Confiable y Seguro', secondary: 'Servicio honesto y confiable con el que puede contar para su protección' },
        emergency: { primary: 'Servicio de Emergencia 24/7', secondary: 'Disponible a toda hora para reparaciones urgentes' },
        pricing: { primary: 'Precios Claros', secondary: 'Sin cargos ocultos ni sorpresas' },
        craftsmen: { primary: 'Trabajo Capacitado', secondary: 'Experiencia práctica en todos los oficios principales' },
        training: { primary: 'Capacitación Continua', secondary: 'Al día con las últimas técnicas y tecnologías' },
      },
    },
    contact: {
      title: 'Solicitar Cotización',
      subtitle: 'Cuéntenos sobre su proyecto y le daremos una cotización detallada dentro de 24 horas.',
      preferredMethod: 'Método de Contacto Preferido *',
      email: 'Correo',
      phone: 'Teléfono',
      both: 'Ambos',
      emailLabel: 'Correo Electrónico',
      phoneLabel: 'Número de Teléfono',
      servicesNeeded: 'Servicios Necesarios *',
      selectAll: 'Seleccione todos los servicios que apliquen a su proyecto',
      other: 'Otro',
      otherService: 'Otro Servicio',
      specifyOther: 'Especifique otro servicio',
      projectPhotos: 'Fotos del Proyecto (Opcional)',
      uploadHelp: 'Suba fotos para ayudarnos a entender mejor su proyecto',
      uploadImages: 'Subir Imágenes',
      projectDetails: 'Detalles del Proyecto',
      projectDetailsPlaceholder:
        'Describa su proyecto en detalle, incluyendo requisitos específicos, plazos o inquietudes...',
      sendRequest: 'Enviar Solicitud',
      urgentTitle: '¿Urgente? Llame Ahora',
      urgentText: 'Para reparaciones de emergencia y situaciones urgentes',
      whatToExpect: 'Qué Esperar',
      responseTimeLabel: 'Tiempo de Respuesta:',
      responseTimeText:
        'Nos comunicaremos con usted dentro de 2 horas en horario laboral, o a primera hora del día siguiente para solicitudes fuera de horario.',
      quoteDeliveryLabel: 'Entrega de Cotización:',
      quoteDeliveryText: 'Cotizaciones detalladas por escrito dentro de 24 horas del contacto inicial.',
      schedulingLabel: 'Programación:',
      schedulingText: 'La mayoría de los proyectos se pueden programar dentro de 48-72 horas de aprobada la cotización.',
      selectedServices: 'Servicios Seleccionados',
      successMessage: '¡Solicitud enviada con éxito! Nos comunicaremos con usted dentro de 2 horas.',
      errors: {
        emailRequired: 'El correo es obligatorio',
        phoneRequired: 'El número de teléfono es obligatorio',
        emailInvalid: 'Ingrese un correo electrónico válido',
        phoneInvalid: 'Ingrese un número de teléfono válido',
        servicesRequired: 'Seleccione al menos un servicio o especifique otro',
      },
    },
    trustIndicators: {
      response: { title: 'Respuesta 24/7', description: 'Siempre disponibles cuando más nos necesita' },
      quotes: { title: 'Precios Claros', description: 'Sin sorpresas, precios transparentes' },
      craftsmanship: { title: 'Trabajo Capacitado', description: 'Un profesional capacitado con experiencia práctica' },
      respectful: { title: 'Respetuoso y Limpio', description: 'Trato su hogar como el mío' },
    },
    howItWorks: {
      request: { title: 'Solicite', description: 'Cuéntenos sobre su proyecto y su método de contacto preferido' },
      schedule: { title: 'Agende', description: 'Le daremos una cotización y agendaremos a su conveniencia' },
      done: { title: 'Listo', description: 'Trabajo profesional completado a su satisfacción' },
    },
    servicesData: {
      plumbing: {
        title: 'Reparaciones de Plomería',
        description: 'Desde llaves que gotean hasta reparación de tuberías, atendemos todas sus necesidades de plomería con precisión y cuidado.',
        bullets: ['Detección y reparación de fugas', 'Instalación de llaves y accesorios', 'Limpieza y destape de desagües', 'Mantenimiento de calentadores de agua'],
      },
      electrical: {
        title: 'Servicios Eléctricos',
        description: 'Trabajo eléctrico seguro y confiable para su hogar. Reparaciones e instalaciones eléctricas sin permiso.',
        bullets: ['Instalación de tomacorrientes e interruptores', 'Montaje de luminarias', 'Instalación de ventiladores de techo', 'Diagnóstico eléctrico'],
      },
      drywall: {
        title: 'Tablarroca y Pintura',
        description: 'Reparación profesional de tablarroca y pintura interior para renovar y restaurar su espacio.',
        bullets: ['Reparación de hoyos y grietas', 'Igualación de textura', 'Pintura interior', 'Trabajo de molduras y zócalos'],
      },
      flooring: {
        title: 'Reparación de Pisos',
        description: 'Reparaciones e instalaciones expertas de pisos de madera, azulejo y laminado.',
        bullets: ['Reemplazo de tablones de madera', 'Reparación de azulejos y rejuntado', 'Instalación de laminado', 'Reparación de contrapisos'],
      },
      smartHome: {
        title: 'Instalación de Hogar Inteligente',
        description: 'Instalación y configuración de dispositivos de hogar inteligente para mayor comodidad y seguridad.',
        bullets: ['Instalación de termostato inteligente', 'Montaje de cámaras de seguridad', 'Configuración de timbre inteligente', 'Integración de automatización del hogar'],
      },
      emergency: {
        title: 'Servicios de Emergencia',
        description: 'Respuesta de emergencia 24/7 para reparaciones urgentes del hogar y mitigación de daños por agua.',
        bullets: ['Reparación de fugas de emergencia', 'Mitigación de daños por agua', 'Diagnóstico de cortes de energía', 'Evaluación de daños por tormenta'],
      },
    },
    reviewsData: {
      r1: '¡Chato fue increíble! Arregló el problema de plomería de nuestra cocina a las pocas horas de llamar. Profesional, limpio y a precio razonable.',
      r2: 'Excelente trabajo eléctrico. Instalaron nuestros nuevos ventiladores de techo y arreglaron varios problemas de tomacorrientes. ¡Muy recomendable!',
      r3: 'La reparación de tablarroca quedó impecable, ni se nota que había daño. El trabajo de pintura también se ve increíble.',
      r4: 'Gran servicio para la reparación de nuestro piso de madera. Igualaron la madera existente a la perfección y dejaron el área de trabajo impecable.',
      r5: 'La instalación del hogar inteligente fue perfecta. Configuraron todo nuestro sistema de seguridad y nos enseñaron a usar todo.',
      r6: 'Llamé por una fuga de agua de emergencia a las 2 a.m. Llegaron en 30 minutos y la arreglaron rápidamente. ¡Salvavidas!',
      r7: 'Servicio excepcional de principio a fin. Profesionales, puntuales y la calidad del trabajo superó las expectativas.',
      r8: 'Muy satisfecho con la instalación del tomacorriente. Trabajo limpio y precio justo. Definitivamente los usaré de nuevo.',
    },
  },
};
